import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';
import { useLab } from '../i18n/LabContext';

const S = {
  page: { padding: '0 0 32px' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: 'var(--text)' },
  sub: { fontSize: 12, color: 'var(--text3)', marginTop: 4 },
  row: { display: 'flex', gap: 10, alignItems: 'center' },
  searchInput: {
    background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)',
    padding: '9px 14px', borderRadius: 10, fontSize: 13, outline: 'none', width: 260,
  },
  btnPrimary: {
    padding: '10px 20px', background: 'linear-gradient(135deg, var(--purple), var(--cyan))',
    border: 'none', color: '#fff', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700,
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '10px 14px', fontSize: 11, color: 'var(--text3)',
    letterSpacing: 1, borderBottom: '1px solid var(--border)', textTransform: 'uppercase',
  },
  td: { padding: '12px 14px', fontSize: 13, color: 'var(--text)', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  trow: { transition: 'background 0.15s', cursor: 'pointer' },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
  },
  modal: {
    background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 20,
    padding: 32, width: 460, maxHeight: '85vh', overflowY: 'auto',
  },
  modalTitle: { fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 20 },
  label: { display: 'block', fontSize: 11, color: 'var(--text2)', letterSpacing: 1, marginBottom: 6 },
  input: {
    width: '100%', background: 'var(--card2)', border: '1px solid var(--border)',
    color: 'var(--text)', padding: '12px 14px', borderRadius: 10, fontSize: 14,
    outline: 'none', boxSizing: 'border-box', marginBottom: 14,
  },
  btnRow: { display: 'flex', gap: 10, marginTop: 6 },
  btnCancel: {
    padding: '11px 20px', background: 'transparent', border: '1px solid var(--border)',
    color: 'var(--text2)', borderRadius: 10, cursor: 'pointer', fontSize: 13,
  },
  btnSuccess: {
    flex: 1, padding: '11px 0', background: 'rgba(0,255,136,0.12)', border: '1px solid var(--green)',
    color: 'var(--green)', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700,
  },
  infoBox: {
    background: 'var(--card2)', borderRadius: 12, padding: '12px 16px', marginBottom: 16,
  },
  info: { fontSize: 12, color: 'var(--text2)', marginBottom: 4 },
  infoVal: { color: 'var(--text)', fontWeight: 600 },
  badge: (bg, fg) => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 11,
    fontWeight: 600, background: bg, color: fg,
  }),
  pagination: { display: 'flex', gap: 6, alignItems: 'center', marginTop: 20, justifyContent: 'center' },
  pgBtn: (active) => ({
    padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
    background: active ? 'rgba(0,212,255,0.15)' : 'var(--card2)',
    border: `1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`,
    color: active ? 'var(--cyan)' : 'var(--text2)',
  }),
};

const fmtDate = ts => ts ? new Date(ts).toLocaleDateString('uz-UZ', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

export default function Clients() {
  const { t } = useLang();
  const { mode: labMode } = useLab();
  const isAllMode = labMode === 'all';

  const fmt = n => `${Number(n || 0).toLocaleString()} ${t('currency')}`;
  const fmtMins = mins => {
    const m = parseInt(mins) || 0;
    if (m < 60) return `${m} ${t('clientMinShort')}`;
    const h = Math.floor(m / 60);
    const rem = m % 60;
    return rem ? `${h}${t('clientHourShort')} ${rem}${t('clientMinShort')}` : `${h} ${t('clientHourFull')}`;
  };

  const [data, setData] = useState({ users: [], total: 0, pages: 1 });
  const [search, setSearch] = useState('');
  const [hasGaming, setHasGaming] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [voucherUser, setVoucherUser] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const [cName, setCName] = useState('');
  const [cPhone, setCPhone] = useState('');
  const [cPassword, setCPassword] = useState('');
  const [cBalance, setCBalance] = useState('');
  const [cPayMethod, setCPayMethod] = useState('cash');

  const [topupAmount, setTopupAmount] = useState('');
  const [topupIsDebt, setTopupIsDebt] = useState(false);
  const [topupMethod, setTopupMethod] = useState('cash');

  const [debtPayAmount, setDebtPayAmount] = useState('');
  const [newPassword, setNewPassword] = useState('');

  const [labs, setLabs] = useState([]);
  const [actLabId, setActLabId] = useState('');
  const [actPackages, setActPackages] = useState([]);
  const [actPkgId, setActPkgId] = useState('');
  const [actResult, setActResult] = useState(null);

  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/owner/clients?search=${encodeURIComponent(search)}&page=${page}`;
      if (hasGaming) url += '&has_gaming=1';
      const r = await api.get(url);
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, page, hasGaming]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setCName(''); setCPhone(''); setCPassword(''); setCBalance(''); setCPayMethod('cash');
    setErr(''); setModal('create');
  };

  const openDetail = async (user) => {
    setSelected(user); setDetail(null); setModal('detail');
    try {
      const r = await api.get(`/owner/clients/${user.id}`);
      setDetail(r.data);
    } catch (e) { console.error(e); }
  };

  const handleCreate = async () => {
    setErr('');
    setSaving(true);
    try {
      await api.post('/owner/clients', {
        name: cName, phone: cPhone, password: cPassword,
        initial_balance: parseFloat(cBalance) || 0,
        payment_method: (parseFloat(cBalance) || 0) > 0 ? cPayMethod : undefined,
      });
      setModal(null);
      load();
    } catch (e) {
      setErr(e.response?.data?.error || t('error'));
    } finally { setSaving(false); }
  };

  const handleTopup = async () => {
    setErr('');
    const amt = parseFloat(topupAmount);
    if (!amt || amt <= 0) { setErr(t('clientAmountPositive')); return; }
    setSaving(true);
    try {
      const r = await api.post(`/owner/clients/${selected.id}/topup`, {
        amount: amt,
        is_debt: topupIsDebt,
        payment_method: topupIsDebt ? undefined : topupMethod,
      });
      const newBalance = r.data.balance;
      const newDebt = r.data.debt;
      setData(prev => ({
        ...prev,
        users: prev.users.map(u => u.id === selected.id ? { ...u, balance: newBalance, debt: newDebt } : u),
      }));
      setSelected(prev => ({ ...prev, balance: newBalance, debt: newDebt }));
      setTopupAmount('');
      setTopupIsDebt(false);
      setModal(null);
    } catch (e) {
      setErr(e.response?.data?.error || t('error'));
    } finally { setSaving(false); }
  };

  const handlePayDebt = async () => {
    setErr('');
    const amt = parseFloat(debtPayAmount);
    if (!amt || amt <= 0) { setErr(t('clientAmountPositive')); return; }
    setSaving(true);
    try {
      const r = await api.post(`/owner/clients/${selected.id}/pay-debt`, { amount: amt });
      const { balance, debt } = r.data;
      setData(prev => ({
        ...prev,
        users: prev.users.map(u => u.id === selected.id ? { ...u, balance, debt } : u),
      }));
      setSelected(prev => ({ ...prev, balance, debt }));
      setDebtPayAmount('');
      setModal(null);
    } catch (e) {
      setErr(e.response?.data?.error || t('error'));
    } finally { setSaving(false); }
  };

  const handlePassword = async () => {
    setErr('');
    if (!newPassword || newPassword.length < 4) { setErr(t('clientPassMinChars')); return; }
    setSaving(true);
    try {
      await api.patch(`/owner/clients/${selected.id}/password`, { password: newPassword });
      setNewPassword('');
      setModal(null);
    } catch (e) {
      setErr(e.response?.data?.error || t('error'));
    } finally { setSaving(false); }
  };

  const openTopup = (user) => {
    setSelected(user); setTopupAmount(''); setTopupIsDebt(false); setTopupMethod('cash'); setErr(''); setModal('topup');
  };

  const openPassword = (user) => {
    setSelected(user); setNewPassword(''); setErr(''); setModal('password');
  };

  const openActivate = async (user) => {
    setSelected(user); setErr(''); setActResult(null);
    setActLabId(''); setActPackages([]); setActPkgId('');
    setModal('activate');
    try {
      const r = await api.get('/owner/labs');
      setLabs(r.data || []);
      if (r.data?.length === 1) loadLabPackages(r.data[0].id);
    } catch (e) { console.error(e); }
  };

  const loadLabPackages = async (labId) => {
    setActLabId(String(labId));
    setActPkgId('');
    try {
      const r = await api.get(`/labs/${labId}`);
      setActPackages(r.data.packages || []);
      if (r.data.packages?.length) setActPkgId(String(r.data.packages[0].id));
    } catch (e) { console.error(e); }
  };

  const handleActivate = async () => {
    setErr(''); setActResult(null);
    if (!actPkgId) { setErr(t('clientSelectPackage')); return; }
    setSaving(true);
    try {
      const r = await api.post(`/owner/clients/${selected.id}/activate-package`, {
        package_id: parseInt(actPkgId),
      });
      setActResult(r.data);
      load();
    } catch (e) {
      setErr(e.response?.data?.error || t('error'));
    } finally { setSaving(false); }
  };

  const pages = Array.from({ length: data.pages }, (_, i) => i + 1);

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>{t('clientsTitle')}</div>
          <div style={S.sub}>{t('clientsTotalUsers').replace('{n}', data.total)}</div>
        </div>
        <div style={S.row}>
          <input
            style={S.searchInput}
            placeholder={t('crmSearchPlh')}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: hasGaming ? 'var(--cyan)' : 'var(--text3)', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={hasGaming} onChange={e => { setHasGaming(e.target.checked); setPage(1); }} style={{ accentColor: 'var(--cyan)' }} />
            Gaming profil
          </label>
          {!isAllMode && (
            <button style={S.btnPrimary} onClick={openCreate}>{t('clientNewBtn')}</button>
          )}
        </div>
      </div>
      {isAllMode && (
        <div style={{
          background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 12,
          padding: '14px 18px', marginBottom: 16, fontSize: 13, color: 'var(--text2)',
        }}>
          🌐 <strong>Hammasi</strong> rejimida mijoz yaratish, topup, qarz to'lash va paket aktivatsiyasi o'chirilgan.
          Yozish amallari uchun yuqoridagi klub tanlagichidan aniq klub tanlang.
        </div>
      )}

      <div style={{ background: 'var(--card)', borderRadius: 16, border: '1px solid var(--border)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--cyan)' }}>{t('loading')}</div>
        ) : data.users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>{t('clientNotFound')}</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>{t('clientColName')}</th>
                <th style={S.th}>{t('crmColPhone')}</th>
                <th style={S.th}>@nick / Steam</th>
                <th style={S.th}>Do'stlar</th>
                <th style={S.th}>Referral</th>
                <th style={S.th}>{t('crmColBalance')}</th>
                <th style={S.th}>{t('crmColSessions')}</th>
                <th style={S.th}>{t('clientColHours')}</th>
                <th style={S.th}>{t('clientColPackage')}</th>
                <th style={S.th}>{t('colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map(u => {
                const vouchers = u.vouchers || [];
                return (
                  <tr key={u.id} style={S.trow}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={S.td} onClick={() => openDetail(u)}>
                      <div style={{ fontWeight: 600 }}>{u.name}</div>
                      <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                        {u.source === 'admin' && (
                          <span style={S.badge('rgba(124,58,237,0.15)', 'var(--purple)')}>admin</span>
                        )}
                        {u.qr_registered && (
                          <span style={S.badge('rgba(0,212,255,0.12)', 'var(--cyan)')} title="QR skaner orqali ro'yxatdan o'tgan">📱 QR</span>
                        )}
                      </div>
                    </td>
                    <td style={S.td}>{u.phone}</td>
                    <td style={S.td}>
                      {u.gamer_tag ? <div style={{ color: 'var(--cyan)', fontWeight: 600, fontSize: 12 }}>@{u.gamer_tag}</div> : <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>}
                      {u.steam_id && <div style={{ color: 'var(--text3)', fontSize: 10 }}>Steam: {u.steam_id}</div>}
                    </td>
                    <td style={S.td}>
                      {u.friends_count > 0
                        ? <span style={{ color: 'var(--cyan)', fontWeight: 600 }}>{u.friends_count}</span>
                        : <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>}
                    </td>
                    <td style={S.td}>
                      {u.referral_code && (
                        <div style={{ color: 'var(--orange)', fontWeight: 600, fontSize: 11, fontFamily: 'monospace' }}>{u.referral_code}</div>
                      )}
                      {u.referral_count > 0 && (
                        <div style={{ color: 'var(--green)', fontSize: 11, marginTop: 2 }}>{u.referral_count} taklif</div>
                      )}
                      {u.referred_by_name && (
                        <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 2 }}>via {u.referred_by_name}</div>
                      )}
                      {!u.referral_code && !u.referred_by_name && <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>}
                    </td>
                    <td style={S.td}>
                      <span style={{ color: Number(u.balance) > 0 ? 'var(--green)' : 'var(--text2)', fontWeight: 600 }}>
                        {fmt(u.balance)}
                      </span>
                      {Number(u.debt) > 0 && (
                        <span style={{ display: 'block', fontSize: 11, color: '#ef4444', fontWeight: 700, marginTop: 2 }}>
                          {t('clientDebt')}: {fmt(u.debt)}
                        </span>
                      )}
                    </td>
                    <td style={S.td} onClick={() => openDetail(u)}>
                      <span style={{ color: 'var(--cyan)', fontWeight: 700 }}>{Number(u.total_sessions || 0)}</span>
                      {t('clientSessionUnit') && <span style={{ color: 'var(--text3)', fontSize: 11 }}> {t('clientSessionUnit')}</span>}
                    </td>
                    <td style={S.td} onClick={() => openDetail(u)}>
                      <span style={{ color: 'var(--text2)', fontWeight: 600 }}>{Number(u.total_hours || 0)}</span>
                      <span style={{ color: 'var(--text3)', fontSize: 11 }}> {t('clientHourUnit')}</span>
                    </td>
                    <td style={S.td}>
                      {vouchers.length > 0 ? (() => {
                        const totalMins = vouchers.reduce((s, v) => s + v.remaining_minutes, 0);
                        const hasActive = vouchers.some(v => v.status === 'active');
                        return (
                          <div
                            onClick={e => { e.stopPropagation(); setVoucherUser(u); setModal('vouchers'); }}
                            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                              background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)',
                              borderRadius: 8, padding: '4px 10px' }}
                          >
                            <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--cyan)' }}>{vouchers.length}</span>
                            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{t('clientPackageSuffix')}</span>
                            <span style={{ width: 1, height: 12, background: 'var(--border)' }} />
                            <span style={{ fontWeight: 600, fontSize: 12, color: 'var(--text2)' }}>{fmtMins(totalMins)}</span>
                            {hasActive && <span style={{ fontSize: 10, color: 'var(--cyan)' }}>🎮</span>}
                          </div>
                        );
                      })() : (
                        <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={S.td}>
                      {isAllMode ? (
                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>🌐 klub tanlang</span>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button onClick={() => openActivate(u)} style={{
                            padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                            background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)', color: 'var(--cyan)',
                          }}>{t('clientActPackageBtn')}</button>
                          <button onClick={() => openTopup(u)} style={{
                            padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                            background: 'rgba(0,255,136,0.1)', border: '1px solid var(--green)', color: 'var(--green)',
                          }}>{t('clientTopupBtn')}</button>
                          <button onClick={() => openPassword(u)} style={{
                            padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 11,
                            background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text2)',
                          }}>{t('clientPasswordBtn')}</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {data.pages > 1 && (
        <div style={S.pagination}>
          {pages.map(p => (
            <button key={p} style={S.pgBtn(p === page)} onClick={() => setPage(p)}>{p}</button>
          ))}
        </div>
      )}

      {/* Create modal */}
      {modal === 'create' && (
        <div style={S.overlay} onClick={() => setModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>{t('clientCreateTitle')}</div>
            <label style={S.label}>{t('clientFieldFullName')}</label>
            <input style={S.input} placeholder="Abdullayev Bobur" value={cName} onChange={e => setCName(e.target.value)} />
            <label style={S.label}>{t('clientFieldPhone')}</label>
            <input style={S.input} placeholder="+998901234567" value={cPhone} onChange={e => setCPhone(e.target.value)} />
            <label style={S.label}>{t('clientFieldPassword')}</label>
            <input style={S.input} type="password" placeholder={t('clientPassMinCharsPlh')} value={cPassword} onChange={e => setCPassword(e.target.value)} />
            <label style={S.label}>{t('clientFieldInitBalance')}</label>
            <input style={S.input} type="number" placeholder="0" value={cBalance} onChange={e => setCBalance(e.target.value)} />
            {Number(cBalance) > 0 && (
              <>
                <label style={S.label}>To'lov usuli</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
                  {[
                    { key: 'cash',     label: '💵 Naqd',     color: 'var(--green)' },
                    { key: 'click',    label: '🔵 Click',    color: 'var(--blue)' },
                    { key: 'payme',    label: '📱 Payme',    color: 'var(--purple)' },
                    { key: 'terminal', label: '💳 Terminal', color: 'var(--cyan)' },
                  ].map(m => {
                    const active = cPayMethod === m.key;
                    return (
                      <button key={m.key} type="button" onClick={() => setCPayMethod(m.key)} style={{
                        padding: '9px 0', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        background: active ? `color-mix(in srgb, ${m.color} 15%, transparent)` : 'var(--card2)',
                        border: `1.5px solid ${active ? m.color : 'var(--border)'}`,
                        color: active ? m.color : 'var(--text2)',
                        transition: 'all 0.15s',
                      }}>{m.label}</button>
                    );
                  })}
                </div>
              </>
            )}
            {err && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
            <div style={S.btnRow}>
              <button style={S.btnCancel} onClick={() => setModal(null)}>{t('cancelShort')}</button>
              <button style={S.btnPrimary} onClick={handleCreate} disabled={saving || !cName || !cPhone || !cPassword}>
                {saving ? '...' : t('clientCreateBtn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Topup modal */}
      {modal === 'topup' && selected && (
        <div style={S.overlay} onClick={() => setModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>{t('clientTopupTitle')}</div>
            <div style={S.infoBox}>
              <div style={S.info}>{t('clientInfoName')} <span style={S.infoVal}>{selected.name}</span></div>
              <div style={S.info}>{t('clientInfoPhone')} <span style={S.infoVal}>{selected.phone}</span></div>
              <div style={S.info}>{t('clientInfoBalance')} <span style={{ ...S.infoVal, color: 'var(--cyan)' }}>{fmt(selected.balance)}</span></div>
              {Number(selected.debt) > 0 && (
                <div style={S.info}>{t('clientDebt')}: <span style={{ ...S.infoVal, color: '#ef4444', fontWeight: 700 }}>{fmt(selected.debt)}</span></div>
              )}
            </div>
            <label style={S.label}>{t('clientTopupAmountLabel')}</label>
            <input style={S.input} type="number" placeholder="50000" value={topupAmount}
              onChange={e => setTopupAmount(e.target.value)} autoFocus />
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              {[10000, 20000, 50000, 100000].map(amt => (
                <button key={amt} onClick={() => setTopupAmount(String(amt))} style={{
                  flex: 1, padding: '7px 0', borderRadius: 8, cursor: 'pointer', fontSize: 11,
                  background: topupAmount === String(amt) ? 'rgba(0,212,255,0.15)' : 'var(--card2)',
                  border: `1px solid ${topupAmount === String(amt) ? 'var(--cyan)' : 'var(--border)'}`,
                  color: topupAmount === String(amt) ? 'var(--cyan)' : 'var(--text2)',
                }}>{(amt / 1000)}K</button>
              ))}
            </div>

            {!topupIsDebt && (
              <>
                <label style={S.label}>To'lov usuli</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
                  {[
                    { key: 'cash',     label: '💵 Naqd',     color: 'var(--green)' },
                    { key: 'click',    label: '🔵 Click',    color: 'var(--blue)' },
                    { key: 'payme',    label: '📱 Payme',    color: 'var(--purple)' },
                    { key: 'terminal', label: '💳 Terminal', color: 'var(--cyan)' },
                  ].map(m => {
                    const active = topupMethod === m.key;
                    return (
                      <button key={m.key} type="button" onClick={() => setTopupMethod(m.key)} style={{
                        padding: '9px 0', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                        background: active ? `color-mix(in srgb, ${m.color} 15%, transparent)` : 'var(--card2)',
                        border: `1.5px solid ${active ? m.color : 'var(--border)'}`,
                        color: active ? m.color : 'var(--text2)',
                        transition: 'all 0.15s',
                      }}>{m.label}</button>
                    );
                  })}
                </div>
              </>
            )}

            <label onClick={() => setTopupIsDebt(v => !v)} style={{
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
              padding: '12px 14px', borderRadius: 10, marginBottom: 14,
              background: topupIsDebt ? 'rgba(239,68,68,0.1)' : 'var(--card2)',
              border: `1.5px solid ${topupIsDebt ? '#ef4444' : 'var(--border)'}`,
              transition: 'all 0.15s',
            }}>
              <div style={{
                width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                border: `2px solid ${topupIsDebt ? '#ef4444' : 'var(--border)'}`,
                background: topupIsDebt ? '#ef4444' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {topupIsDebt && <span style={{ color: '#fff', fontSize: 12, fontWeight: 800 }}>✓</span>}
              </div>
              <div>
                <div style={{ fontSize: 13, color: topupIsDebt ? '#ef4444' : 'var(--text)', fontWeight: topupIsDebt ? 700 : 400 }}>
                  {t('clientTopupAsDebt')}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  {t('clientTopupDebtNote')}
                </div>
              </div>
            </label>

            {err && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
            <div style={S.btnRow}>
              <button style={S.btnCancel} onClick={() => setModal(null)}>{t('cancelShort')}</button>
              <button onClick={handleTopup} disabled={saving || !topupAmount} style={{
                flex: 1, padding: '11px 0', borderRadius: 10, cursor: saving || !topupAmount ? 'not-allowed' : 'pointer',
                fontSize: 14, fontWeight: 700, border: 'none',
                background: topupIsDebt
                  ? (saving || !topupAmount ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.2)')
                  : (saving || !topupAmount ? 'var(--card2)' : 'rgba(0,255,136,0.12)'),
                color: topupIsDebt ? '#ef4444' : 'var(--green)',
              }}>
                {saving ? '...' : topupIsDebt
                  ? `${t('clientDebt')}: ${topupAmount ? Number(topupAmount).toLocaleString() : '0'} ${t('currency')}`
                  : `+ ${topupAmount ? Number(topupAmount).toLocaleString() : '0'} ${t('currency')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pay debt modal */}
      {modal === 'paydebt' && selected && (
        <div style={S.overlay} onClick={() => setModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>{t('clientPayDebtTitle')}</div>
            <div style={S.infoBox}>
              <div style={S.info}>{t('clientInfoName')} <span style={S.infoVal}>{selected.name}</span></div>
              <div style={S.info}>{t('clientTotalDebt')} <span style={{ ...S.infoVal, color: '#ef4444', fontWeight: 700 }}>{fmt(selected.debt)}</span></div>
            </div>
            <label style={S.label}>{t('clientPayAmountLabel')}</label>
            <input style={S.input} type="number" placeholder="0" value={debtPayAmount}
              onChange={e => setDebtPayAmount(e.target.value)} autoFocus />
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <button onClick={() => setDebtPayAmount(String(selected.debt))} style={{
                flex: 1, padding: '7px 0', borderRadius: 8, cursor: 'pointer', fontSize: 11,
                background: debtPayAmount === String(selected.debt) ? 'rgba(239,68,68,0.15)' : 'var(--card2)',
                border: `1px solid ${debtPayAmount === String(selected.debt) ? '#ef4444' : 'var(--border)'}`,
                color: debtPayAmount === String(selected.debt) ? '#ef4444' : 'var(--text2)',
              }}>{t('clientPayDebtFull')} ({Number(selected.debt).toLocaleString()})</button>
            </div>
            {err && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
            <div style={S.btnRow}>
              <button style={S.btnCancel} onClick={() => setModal(null)}>{t('cancelShort')}</button>
              <button onClick={handlePayDebt} disabled={saving || !debtPayAmount} style={{
                flex: 1, padding: '11px 0', borderRadius: 10,
                cursor: saving || !debtPayAmount ? 'not-allowed' : 'pointer',
                fontSize: 14, fontWeight: 700, border: '1px solid #ef4444',
                background: 'rgba(239,68,68,0.15)', color: '#ef4444',
              }}>
                {saving ? '...' : `✓ ${debtPayAmount ? Number(debtPayAmount).toLocaleString() : '0'} ${t('currency')} ${t('clientPayAccepted')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password modal */}
      {modal === 'password' && selected && (
        <div style={S.overlay} onClick={() => setModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>{t('clientPasswordTitle')}</div>
            <div style={S.infoBox}>
              <div style={S.info}>{t('clientInfoName')} <span style={S.infoVal}>{selected.name}</span></div>
              <div style={S.info}>{t('clientInfoPhone')} <span style={S.infoVal}>{selected.phone}</span></div>
            </div>
            <label style={S.label}>{t('clientNewPasswordLabel')}</label>
            <input style={S.input} type="text" placeholder={t('clientNewPasswordPlh')} value={newPassword}
              onChange={e => setNewPassword(e.target.value)} autoFocus />
            {err && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
            <div style={S.btnRow}>
              <button style={S.btnCancel} onClick={() => setModal(null)}>{t('cancelShort')}</button>
              <button style={S.btnPrimary} onClick={handlePassword} disabled={saving}>
                {saving ? '...' : `✓ ${t('save')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {modal === 'detail' && selected && (
        <div style={S.overlay} onClick={() => setModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>📋 {selected.name}</div>
            <div style={S.infoBox}>
              <div style={S.info}>{t('clientInfoPhone')} <span style={S.infoVal}>{selected.phone}</span></div>
              <div style={S.info}>{t('clientInfoBalance').replace(':', '')}:{' '}
                <span style={{ ...S.infoVal, color: 'var(--cyan)' }}>{fmt(selected.balance)}</span>
              </div>
              {Number(selected.debt) > 0 && (
                <div style={{ ...S.info, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    {t('clientDebt')}: <span style={{ ...S.infoVal, color: '#ef4444', fontWeight: 700 }}>{fmt(selected.debt)}</span>
                    <span style={{ fontSize: 11, color: '#f87171', marginLeft: 8 }}>⚠ {t('clientDebtUnpaid')}</span>
                  </div>
                  <button onClick={() => { setDebtPayAmount(String(selected.debt)); setErr(''); setModal('paydebt'); }} style={{
                    background: 'rgba(239,68,68,0.15)', border: '1px solid #ef4444', color: '#ef4444',
                    padding: '5px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700,
                  }}>{t('clientPayDebtBtn')}</button>
                </div>
              )}
              <div style={S.info}>{t('clientTotalSessions')} <span style={S.infoVal}>{selected.total_sessions}</span></div>
              <div style={S.info}>{t('clientTotalSpent')} <span style={S.infoVal}>{fmt(selected.total_spent)}</span></div>
              <div style={S.info}>{t('clientRegistered')} <span style={S.infoVal}>{fmtDate(selected.created_at)}</span></div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
              <button style={{
                flex: 1, padding: '11px 0', background: 'rgba(0,212,255,0.12)', border: '1px solid var(--cyan)',
                color: 'var(--cyan)', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700,
              }} onClick={() => { setModal(null); setTimeout(() => openActivate(selected), 50); }}>
                {t('clientActivatePackage')}
              </button>
              <button style={S.btnSuccess} onClick={() => { setModal(null); setTimeout(() => openTopup(selected), 50); }}>
                {t('clientTopupBtn')}
              </button>
              <button style={{
                padding: '11px 16px', background: 'var(--card2)', border: '1px solid var(--border)',
                color: 'var(--text2)', borderRadius: 10, cursor: 'pointer', fontSize: 13,
              }} onClick={() => { setModal(null); setTimeout(() => openPassword(selected), 50); }}>
                {t('clientPasswordBtnShort')}
              </button>
            </div>

            <div style={{ fontSize: 11, color: 'var(--text3)', letterSpacing: 1, marginBottom: 10 }}>
              {t('clientLastSessions')}
            </div>
            {!detail ? (
              <div style={{ color: 'var(--text3)', fontSize: 12 }}>{t('loading')}</div>
            ) : detail.sessions?.length === 0 ? (
              <div style={{ color: 'var(--text3)', fontSize: 12 }}>{t('clientNoSessions')}</div>
            ) : (
              detail.sessions?.map(s => (
                <div key={s.id} style={{ ...S.infoBox, marginBottom: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>{fmtDate(s.started_at)}</div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--cyan)' }}>{fmt(s.amount_paid)}</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                    {s.lab_name} · {s.package_name || t('clientHourly')} · {s.payment_type}
                  </div>
                </div>
              ))
            )}

            <button style={{ ...S.btnCancel, width: '100%', marginTop: 10 }} onClick={() => setModal(null)}>{t('clientCloseBtn')}</button>
          </div>
        </div>
      )}

      {/* Activate package modal */}
      {modal === 'activate' && selected && (
        <div style={S.overlay} onClick={() => !actResult && setModal(null)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>{t('clientActivatePackageTitle')}</div>

            {actResult ? (
              <div>
                <div style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid var(--green)', borderRadius: 14, padding: 24, marginBottom: 20, textAlign: 'center' }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>✓</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--green)', marginBottom: 6 }}>{t('clientVoucherCreated')}</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>
                    {t('clientVoucherInst1')}<br/>
                    <strong style={{ color: 'var(--text)' }}>{t('clientVoucherInst2')}</strong> —<br/>
                    {t('clientVoucherInst3')}
                  </div>
                </div>
                <div style={S.infoBox}>
                  <div style={S.info}>{t('clientInfoName')} <span style={S.infoVal}>{actResult.user_name}</span></div>
                  <div style={S.info}>{t('clientInfoPackage')} <span style={S.infoVal}>{actResult.package_name}</span></div>
                  <div style={S.info}>{t('clientInfoDuration')} <span style={S.infoVal}>{actResult.duration_minutes} {t('clientMinuteUnit')}</span></div>
                  <div style={S.info}>{t('clientInfoPaid')} <span style={{ ...S.infoVal, color: 'var(--red)' }}>−{Number(actResult.price).toLocaleString()} {t('currency')}</span></div>
                  <div style={S.info}>{t('clientInfoRemainingBalance')} <span style={{ ...S.infoVal, color: 'var(--cyan)' }}>{Number(actResult.new_balance).toLocaleString()} {t('currency')}</span></div>
                  <div style={S.info}>{t('clientInfoValidFor')} <span style={S.infoVal}>24 {t('clientHourFull')}</span></div>
                </div>
                <button style={{ ...S.btnPrimary, width: '100%', marginTop: 8 }} onClick={() => setModal(null)}>{t('clientCloseBtn')}</button>
              </div>
            ) : (
              <>
                <div style={S.infoBox}>
                  <div style={S.info}>{t('clientInfoName')} <span style={S.infoVal}>{selected.name}</span></div>
                  <div style={S.info}>{t('clientInfoPhone')} <span style={S.infoVal}>{selected.phone}</span></div>
                  <div style={S.info}>{t('clientInfoBalance').replace(':', '')}:{' '}
                    <span style={{ ...S.infoVal, color: Number(selected.balance) > 0 ? 'var(--cyan)' : 'var(--red)' }}>{fmt(selected.balance)}</span>
                  </div>
                </div>

                {false && labs.length > 1 && (
                  <>
                    <label style={S.label}>{t('clientRoomLabel')}</label>
                    <select style={S.input} value={actLabId}
                      onChange={e => loadLabPackages(e.target.value)}>
                      <option value="">{t('clientSelectRoom')}</option>
                      {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </>
                )}

                <label style={S.label}>{t('clientSelectPackageLabel')}</label>
                {actPackages.length === 0 ? (
                  <div style={{ color: 'var(--text3)', fontSize: 12, marginBottom: 14 }}>
                    {actLabId ? t('clientNoPackages') : t('loading')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                    {actPackages.map(p => {
                      const canAfford = parseFloat(selected.balance) >= parseFloat(p.price);
                      return (
                        <button key={p.id} onClick={() => setActPkgId(String(p.id))} style={{
                          padding: '13px 16px', borderRadius: 12, cursor: 'pointer', textAlign: 'left',
                          background: actPkgId === String(p.id) ? 'rgba(0,212,255,0.1)' : 'var(--card2)',
                          border: `2px solid ${actPkgId === String(p.id) ? 'var(--cyan)' : 'var(--border)'}`,
                          color: 'var(--text)', transition: 'all 0.15s',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</div>
                            <div style={{ fontSize: 15, fontWeight: 800, color: canAfford ? 'var(--green)' : 'var(--red)' }}>
                              {Number(p.price).toLocaleString()} {t('currency')}
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                            ⏱ {p.duration_minutes} {t('clientMinuteUnit')}
                            {!canAfford && <span style={{ color: 'var(--red)', marginLeft: 10 }}>{t('clientInsufficientBalance')}</span>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                <div style={{ background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
                  {t('clientActivateHint')}
                </div>

                {err && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{err}</div>}

                <div style={S.btnRow}>
                  <button style={S.btnCancel} onClick={() => setModal(null)}>{t('cancelShort')}</button>
                  <button style={S.btnPrimary} onClick={handleActivate} disabled={saving || !actPkgId}>
                    {saving ? '...' : t('clientActivateBtn')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Vouchers modal */}
      {modal === 'vouchers' && voucherUser && (
        <div style={S.overlay} onClick={() => setModal(null)}>
          <div style={{ ...S.modal, maxWidth: 420 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalTitle}>🎮 {voucherUser.name} — {t('clientPackagesTitle')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
              {(voucherUser.vouchers || []).map((v, i) => {
                const pct = Math.round((v.remaining_minutes / v.duration_minutes) * 100);
                const col = pct > 50 ? 'var(--green)' : pct > 20 ? 'var(--orange)' : 'var(--red)';
                return (
                  <div key={v.id} style={{
                    background: 'var(--bg2)', border: `1px solid ${v.status === 'active' ? 'rgba(0,212,255,0.3)' : 'var(--border)'}`,
                    borderRadius: 10, padding: '12px 14px',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text1)' }}>
                        {i + 1}. {v.package_name}
                      </div>
                      {v.status === 'active'
                        ? <span style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 600, background: 'rgba(0,212,255,0.1)', padding: '2px 8px', borderRadius: 6 }}>🎮 PC#{v.active_pc}</span>
                        : <span style={{ fontSize: 11, color: 'var(--text3)', background: 'var(--bg3)', padding: '2px 8px', borderRadius: 6 }}>{t('clientVoucherWaiting')}</span>
                      }
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>{t('clientVoucherRemaining')}</span>
                      <span style={{ fontWeight: 700, fontSize: 15, color: col }}>{fmtMins(v.remaining_minutes)}</span>
                      <span style={{ fontSize: 11, color: 'var(--text3)' }}>/ {fmtMins(v.duration_minutes)}</span>
                    </div>
                    <div style={{ height: 4, background: 'var(--border)', borderRadius: 4 }}>
                      <div style={{ height: 4, borderRadius: 4, width: `${pct}%`, background: col, transition: 'width 0.3s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: '1px solid var(--border)', marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>{t('clientVoucherTotalRemaining')}</span>
              <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--cyan)' }}>
                {fmtMins((voucherUser.vouchers || []).reduce((s, v) => s + v.remaining_minutes, 0))}
              </span>
            </div>
            <button style={{ ...S.btnPrimary, width: '100%' }} onClick={() => setModal(null)}>{t('clientCloseBtn')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
